import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBoards, getMediums, getGrades, getSections } from "../nodeApi/getApi/getMastersApi";
import { postBoard, postMedium, postGrade, postSection } from "../nodeApi/postApi/postMastersApi";
import { putBoard, putMedium, putGrade, putSection } from "../nodeApi/putApi/putMastersApi";
import { patchBoardStatus, patchMediumStatus, patchGradeStatus, patchSectionStatus } from "../nodeApi/patchApi/patchMastersApi";
import { useToaster } from "../../shared/utils/ToasterProvider";

// Queries
export const useGetBoards = (filters) => {
  return useQuery({
    queryKey: ["masters", "boards", filters],
    queryFn: () => getBoards(filters),
  });
};

export const useGetMediums = (filters) => {
  return useQuery({
    queryKey: ["masters", "mediums", filters],
    queryFn: () => getMediums(filters),
  });
};

export const useGetGrades = (filters) => {
  return useQuery({
    queryKey: ["masters", "grades", filters],
    queryFn: () => getGrades(filters),
  });
};

export const useGetSections = (filters) => {
  return useQuery({
    queryKey: ["masters", "sections", filters],
    queryFn: () => getSections(filters),
  });
};

// Mutations - Boards
export const useCreateBoard = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postBoard,
    onSuccess: (data) => {
      showToaster(data?.message || "Board created successfully", "", "success");
      queryClient.invalidateQueries(["masters", "boards"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to create board", "", "error");
    },
  });
};

export const useUpdateBoard = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => putBoard(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Board updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "boards"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update board", "", "error");
    },
  });
};

export const useUpdateBoardStatus = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => patchBoardStatus(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Board status updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "boards"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update board status", "", "error");
    },
  });
};

// Mutations - Mediums
export const useCreateMedium = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postMedium,
    onSuccess: (data) => {
      showToaster(data?.message || "Medium created successfully", "", "success");
      queryClient.invalidateQueries(["masters", "mediums"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to create medium", "", "error");
    },
  });
};

export const useUpdateMedium = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => putMedium(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Medium updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "mediums"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update medium", "", "error");
    },
  });
};

export const useUpdateMediumStatus = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => patchMediumStatus(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Medium status updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "mediums"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update medium status", "", "error");
    },
  });
};

// Mutations - Grades
export const useCreateGrade = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postGrade,
    onSuccess: (data) => {
      showToaster(data?.message || "Grade created successfully", "", "success");
      queryClient.invalidateQueries(["masters", "grades"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to create grade", "", "error");
    },
  });
};

export const useUpdateGrade = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => putGrade(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Grade updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "grades"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update grade", "", "error");
    },
  });
};

export const useUpdateGradeStatus = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => patchGradeStatus(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Grade status updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "grades"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update grade status", "", "error");
    },
  });
};

// Mutations - Sections
export const useCreateSection = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postSection,
    onSuccess: (data) => {
      showToaster(data?.message || "Section created successfully", "", "success");
      queryClient.invalidateQueries(["masters", "sections"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to create section", "", "error");
    },
  });
};

export const useUpdateSection = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => putSection(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Section updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "sections"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update section", "", "error");
    },
  });
};

export const useUpdateSectionStatus = (onSuccessCallback) => {
  const { showToaster } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => patchSectionStatus(id, payload),
    onSuccess: (data) => {
      showToaster(data?.message || "Section status updated successfully", "", "success");
      queryClient.invalidateQueries(["masters", "sections"]);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    onError: (error) => {
      showToaster(error?.response?.data?.message || "Failed to update section status", "", "error");
    },
  });
};
